import React, {
  useCallback,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactFileUploadWithNotes from 'shared/design-system/components/TrueImpactFileUploadWithNotes';
import { BottomBar } from '../BottomBar';
import {
  BudgeComponentProps,
  BudgetDollarComponent,
  BudgetSubmitReference,
} from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { fileUpload } from '../IndicatorDetails/utils';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

const MAX_FILE_SIZE = 512 * 1000 * 1000; // 512mb
const MAX_NOTE_CHARS = 500;
const acceptedFileString = 'DOC, DOCX, PDF, XLSX, CSV';
const allowedFileExtensions = [
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const StyledDiv = styled.div`
  text-align: center;
  .question-text {
    max-width: 574px;
    margin-top: 48px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .sub-heading {
    max-width: 502px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .file-upload-box {
    justify-content: center;
  }
`;

type BudgetUploadProps = Omit<
  BudgeComponentProps & BudgetDollarComponent,
  'onNext' | 'initialValue'
> & {
  initialValue: TrueImpactFile | undefined;
  onNext: (
    filesJson: string,
    description: string | undefined,
    _id: number | undefined,
    _url: string | undefined,
  ) => unknown;
  // Optional reactive emitter for parents that persist through their own
  // footer instead of this component's BottomBar. Fires only after user
  // interaction, never on initial mount.
  onValueChange?: (filesJson: string, description: string | undefined) => void;
};

const BudgetUpload = React.forwardRef<BudgetSubmitReference, BudgetUploadProps>(
  (
    {
      reportId,
      initialValue,
      guidanceText,
      hideBottomBar,
      onNext,
      onPrevious,
      onValueChange,
    },
    forwardRef,
  ) => {
    const didMountRef = React.useRef(false);
    const [files, setFiles] = useState<TrueImpactFile[]>(() => {
      if (Array.isArray(initialValue)) return initialValue;
      if (initialValue) return [initialValue];
      return [];
    });
    const [description, setDescription] = useState<string>(
      initialValue?.description || '',
    );
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | undefined>(
      undefined,
    );

    useEffect(() => {
      const isValidSingleFile =
        initialValue &&
        typeof initialValue === 'object' &&
        typeof initialValue.fileName === 'string' &&
        initialValue.fileName.trim().length > 0;

      const fallback = isValidSingleFile ? [initialValue] : [];

      try {
        const parsed =
          typeof initialValue?.fileName === 'string'
            ? JSON.parse(initialValue.fileName)
            : null;
        const isParsedFileArray =
          Array.isArray(parsed) &&
          parsed.length > 0 &&
          parsed.every(
            f => f && typeof f === 'object' && typeof f.fileName === 'string',
          );
        if (isParsedFileArray) {
          setFiles(parsed);
        } else if (Array.isArray(parsed) && parsed.length === 0) {
          setFiles([]);
        } else {
          setFiles(fallback);
        }
      } catch (_ex) {
        // Malformed JSON fallback to legacy single file object
        setFiles(fallback);
      }
    }, [initialValue]);

    useEffect(() => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }
      onValueChange?.(
        JSON.stringify(
          files.map(f => ({
            fileName: f.fileName,
            description: f.description,
            id: f.id,
            url: f.url,
          })),
        ),
        description,
      );
    }, [files, description]);

    const handleNext = () =>
      onNext(
        JSON.stringify(
          files.map(f => ({
            fileName: f.fileName,
            description: f.description,
            id: f.id,
            url: f.url,
          })),
        ),
        description,
        undefined,
        undefined,
      );

    useImperativeHandle(forwardRef, () => ({
      error: () => undefined,
      handleSubmit: () =>
        JSON.stringify({
          files: files.map(f => ({
            fileName: f.fileName,
            description: f.description,
            id: f.id,
            url: f.url,
          })),
          description,
        }),
    }));

    const handleFileUpload = useCallback(
      async (file: File | undefined) => {
        if (!file || files.length >= 10) return;

        const alreadyExists = files.some(f => f.fileName === file.name);
        if (alreadyExists) {
          setUploadError(`"${file.name}" has already been uploaded.`);
          return;
        }

        setUploadError(undefined); // clear previous error
        setIsUploading(true);
        const uploadResult = await fileUpload(reportId, file);
        setIsUploading(false);

        setFiles(prev => [
          ...prev,
          {
            ...uploadResult,
            file,
            id: uploadResult?.id,
            url: uploadResult?.url || '',
            fileName: file.name,
            description,
            error: undefined,
          },
        ]);
      },
      [reportId, files, description],
    );

    const handleDelete = (fileName: string) => {
      setFiles(prev => prev.filter(f => f.fileName !== fileName));
    };

    return (
      <StyledDiv>
        <TrueImpactText variant="heading-page" className="question-text">
          Would you like to upload any documents or provide additional
          information to support your budget numbers?
        </TrueImpactText>
        <ShowGuidanceText
          sectionId="budget"
          questionId={80009}
          guidanceText={guidanceText}
        />
        <TrueImpactText className="sub-heading">
          This isn’t required but can be helpful to have this information saved
          to provide context or year-over-year analysis.
        </TrueImpactText>
        <TrueImpactFileUploadWithNotes
          files={files}
          setFiles={setFiles}
          acceptedFileString={acceptedFileString}
          maxFileSize={MAX_FILE_SIZE}
          allowedFileExtensions={allowedFileExtensions}
          placeholder="Add any notes about these documents here..."
          handleFileUpload={handleFileUpload}
          handleDelete={handleDelete}
          description={description || ''}
          setDescription={setDescription}
          className="file-upload-box"
          isUploading={isUploading}
          errorMessage={uploadError}
          maxChars={MAX_NOTE_CHARS}
        />
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={false}
            handleBack={onPrevious}
            handleNext={handleNext}
            backTitle="Back"
            nextTitle="Save & Continue"
          />
        )}
      </StyledDiv>
    );
  },
);

export default BudgetUpload;
